import styled from "styled-components";
import { useParams } from "react-router-dom";
import SEO from "src/SEO";
import Hero from "src/components/Hero/Hero";
import { Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import ReactMarkdown from "react-markdown";
import EventFinished from "src/components/EventFinished";
import useResponsiveImageUrl from "src/utils/useResponsiveImageUrl";
import DynamicGoogleFormSubmitter from "src/components/Form/DynamicGoogleFormSubmitter";
import Button from "src/components/Button/Button";

// Consolidated promo configuration
const PROMO_CONFIG = {
  dragon: {
    sheetName: "Dragon 1",
    entryNumber: "1",
    seoKey: "scavengerHuntDragon2025",
  },
  "big-wheel": {
    sheetName: "Wheel 2",
    entryNumber: "2",
    seoKey: "scavengerHuntBigWheel2025",
    frSlug: "grande-roue",
  },
  "mo-mummy": {
    sheetName: "Mummy 3",
    entryNumber: "3",
    seoKey: "scavengerHuntMoMummy2025",
  },
  "gift-redemption": {
    sheetName: "Gift 4",
    entryNumber: "4",
    seoKey: "scavengerHuntGiftRedemption2025",
    frSlug: "remise-des-cadeaux2025",
  },
  elevator: {
    sheetName: "Elevator 5",
    entryNumber: "5",
    seoKey: "scavengerHuntElevator2025",
    frSlug: "ascenseur",
  },
  billboard: {
    sheetName: "Billboard 6",
    // entryNumber: null, // Standalone page
    seoKey: "scavengerHuntBillboard2025",
    frSlug: "panneau",
  },
  mailer: {
    sheetName: "Mailer 7",
    // entryNumber: null, // Standalone page
    seoKey: "scavengerHuntMailer2025",
    frSlug: "courrier",
  },
};

// Build reverse lookup for French slugs
const FR_TO_EN_SLUG = Object.entries(PROMO_CONFIG).reduce((acc, [key, config]) => {
  if (config.frSlug) {
    acc[config.frSlug] = key;
  }
  return acc;
}, {});

const ScavengerHuntDynamic2025 = () => {
  const { promoName: rawPromoName } = useParams();
  const { t, i18n } = useTranslation();

  // Normalize promo name (convert French slug to English key if needed)
  const promoName = FR_TO_EN_SLUG[rawPromoName] || rawPromoName;

  // Validate promo name exists and get config
  const promoConfig = PROMO_CONFIG[promoName];

  if (!promoConfig) {
    return (
      <ErrorDiv>
        <h2>{t("scavengerHuntDynamic2025.common.error.title")}</h2>
        <p>{t("scavengerHuntDynamic2025.common.error.message")}</p>
      </ErrorDiv>
    );
  }

  const { sheetName, entryNumber, seoKey } = promoConfig;

  return (
    <ScavengerHuntDiv>
      <SEO />
      <EventFinished
        scheduleKeyName={`scavengerHuntDynamic2025_${promoName}`}
        eventType="promotion"
      />
      <Hero
        bgImage="promotions/gaming-machines/win-a-million-2025/WAM-Scavenger-hunt-header-hero-image-2400x800px.jpg"
        image={`promotions/gaming-machines/win-a-million-2025/WAM-Scavenger-hint-logo-2400x2400px-${i18n.language.toUpperCase()}.png`}
        imageAlt="Win a Million Scavenger Hunt Logo"
        fluidImage={false}
      />

      <MainContentDiv>
        <container-div>
          <MainTitle>
            {t("scavengerHuntDynamic2025.common.content.title")}
          </MainTitle>
          <EntryBadge>
            {/* Only show entry number if it exists in config */}
            {entryNumber && t("scavengerHuntDynamic2025.common.content.entryNumber", {
              number: entryNumber,
            })}
          </EntryBadge>
          <DescriptionText>
            <ReactMarkdown>
              {t("scavengerHuntDynamic2025.common.content.description")}
            </ReactMarkdown>
          </DescriptionText>

          <ClaimYourEntryTitle>
            {t("scavengerHuntDynamic2025.common.content.claimYourEntry")}
          </ClaimYourEntryTitle>
        </container-div>
        <Row>
          <Col xs={12} className="mx-auto">
            <FormWrapper>
              <DynamicGoogleFormSubmitter
                formId="AKfycbyIDRvIn0qViMIoKJB9DWJIz2-0Pw9N8rsmBBHOnwDAArse1cRVjkkfFdmz6O0Vb4EPgw" // v4.7 Mailer added
                sheetName={sheetName}
                formTextKey="scavengerHuntDynamic2025.common"
                titles={false}
                formFields={[
                  { id: "FirstName", label: "firstName", type: "text", required: true },
                  { id: "LastName", label: "lastName", type: "text", required: true },
                  { id: "Email", label: "email", type: "email", required: true },
                  { id: "PhoneNumber", label: "phoneNumber", type: "text", required: true },
                  { id: "PlayerID", label: "pgPlayerId", type: "text", required: true, placeholder: "pgPlayerIdPlaceholder" },
                ]}
              />
            </FormWrapper>
          </Col>
        </Row>
        <Row className="small pt-4 pb-4">
          <Col xs={12} md={12} lg={8} className="mx-auto">
            <FormSmallPrint>
              <ReactMarkdown>
                {t("scavengerHuntDynamic2025.common.content.formSmallPrint")}
              </ReactMarkdown>
            </FormSmallPrint>
          </Col>
        </Row>

        <container-div>
          <Row className="g-4 mb-4 mt-4">
            <Col xs={12} md={12} lg={10} className="mx-auto">
              <ContentCard
                bgImage={useResponsiveImageUrl(
                  "promotions/gaming-machines/win-a-million-2025/sparkle-box.png"
                )}
              >
                <CardHeader>
                  {t("scavengerHuntDynamic2025.common.content.aboutWinAMillion")}
                </CardHeader>
                <CardContent>
                  <ReactMarkdown>
                    {t("scavengerHuntDynamic2025.common.content.aboutWinAMillionText")}
                  </ReactMarkdown>
                  <StyledButtonWrapper>
                    <Button
                      bgColor="#c9a961"
                      color="var(--black)"
                      hoverBgColor="var(--white)"
                      hoverColor="var(--black)"
                      label={t("button.learn-more")}
                      href={t("routes.winAMillion2025")}
                      width="wide"
                      size="large"
                    />
                  </StyledButtonWrapper>
                </CardContent>
              </ContentCard>
            </Col>
          </Row>
        </container-div>
      </MainContentDiv>
    </ScavengerHuntDiv>
  );
};

const ScavengerHuntDiv = styled.div`
  --topspacing: 110px;
  --gradientheight: 40px;

  width: 100%;
  height: auto;
  background-color: var(--lightblack);
  color: var(--white);
  text-align: center;
  overflow-x: hidden;
  padding-bottom: 40px;

  .hero_container {
    padding-top: 0;
  }

  .hero_text_container {
    display: none;
    padding: 0;
  }

  .hero_image_container {
    img {
      margin-top: -2rem;
      margin-bottom: 2rem;
      transform: scale3d(1.3, 1.3, 1);
      @media (max-width: 768px) {
        margin-top: -3rem;
        margin-bottom: 3rem;
        min-height: 100px;
      }
    }
    min-height: 600px;
    @media (max-width: 768px) {
      min-height: 100px;
    }
  }

  .title-section {
    h2 {
      text-transform: uppercase;
      color: #f0cb43;
    }
    p {
      max-width: unset;
    }
  }

  .small {
    text-align: center;
  }

  strong {
    color: #f0cb43;
  }

  p,
  li {
    strong {
      color: #f0cb43;
    }
  }
`;

const ErrorDiv = styled.div`
  width: 100%;
  height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: var(--lightblack);
  color: var(--white);

  h2 {
    color: #f0cb43;
    font-size: 2rem;
    margin-bottom: 1rem;
  }

  p {
    font-size: 1.2rem;
  }
`;

const MainTitle = styled.h2`
  color: #f0cb43;
  font-size: 2.5rem;
  font-weight: 700;
  text-transform: uppercase;
  text-align: center;
  margin-bottom: 1rem;
  letter-spacing: 0.1em;

  @media (max-width: 768px) {
    font-size: 1.8rem;
  }
`;

const EntryBadge = styled.div`
  color: var(--white);
  font-size: 1.3rem;
  font-weight: 600;
  text-align: center;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

const DescriptionText = styled.div`
  color: var(--white);
  font-size: 1.1rem;
  line-height: 1.6;
  text-align: center;
  max-width: 900px;
  margin: 0 auto 3rem;

  p {
    margin-bottom: 1rem;

    &:last-child {
      margin-bottom: 0;
    }
  }

  strong {
    color: #f0cb43;
  }

  @media (max-width: 768px) {
    font-size: 1rem;
    margin-bottom: 2rem;
  }
`;

const ClaimYourEntryTitle = styled.h3`
  color: #f0cb43;
  font-size: 2rem;
  font-weight: 700;
  text-transform: uppercase;
  text-align: center;
  margin-bottom: 0.5rem;

  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const FormSmallPrint = styled.div`
  color: var(--white);
  font-size: 0.9rem;
  line-height: 1.5;
  text-align: center;

  p {
    margin-bottom: 0.75rem;

    &:last-child {
      margin-bottom: 0;
    }
  }

  strong {
    color: #f0cb43;
  }

  a {
    color: #f0cb43;
    text-decoration: underline;

    &:hover {
      color: var(--white);
    }
  }

  @media (max-width: 768px) {
    font-size: 0.85rem;
  }
`;

const MainContentDiv = styled.div`
  padding-bottom: 1rem;
  background-color: var(--lightblack);

  @media (min-width: 768px) {
    padding: 0 1rem 1rem;
  }

  @media (min-width: 992px) {
    padding: 0 0 3rem;
  }

  height: auto;
  width: 100%;

  container-div {
    max-width: 1200px;
  }

  .row {
    display: flex;
    justify-content: space-between;
    max-width: 1200px;
    margin: 0 auto;
    position: relative;
  }
`;

const ContentCard = styled.div`
  background: url(${(props) => props.bgImage}) no-repeat;
  background-size: cover;
  background-position: 70% center;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const CardHeader = styled.div`
  background: linear-gradient(135deg, #d4af37 0%, #f4e4a6 50%, #d4af37 100%);
  color: var(--black);
  padding: 0.2rem 1.5rem;
  font-size: 1.5rem;
  font-weight: 700;
  text-transform: uppercase;
  text-align: center;

  @media (max-width: 768px) {
    font-size: 1rem;
    padding: 0.75rem 1rem;
  }
`;

const CardContent = styled.div`
  padding: 1.5rem;
  color: var(--white);
  flex: 1;
  text-align: center;

  p {
    margin-bottom: 0.75rem;
    line-height: 1.6;

    &:last-child {
      margin-bottom: 0;
    }
  }

  strong {
    color: #f0cb43;
    font-weight: 600;
  }

  ul,
  ol {
    margin: 0.5rem 0;
    padding-left: 1.5rem;
    text-align: left;

    li {
      margin-bottom: 0.5rem;
      line-height: 1.6;
    }
  }

  a {
    display: inline-block;
    margin-top: 1rem;
  }

  @media (max-width: 768px) {
    padding: 1rem;
    font-size: 0.95rem;
  }
`;

const FormWrapper = styled.div`
  width: 100%;
  max-width: 600px;
  margin: 0 auto;

  /* Left align form labels */
  .form-container .form-label,
  .form-container label {
    text-align: left !important;
    justify-content: flex-start !important;
    align-items: flex-start !important;
    display: block !important;
  }

  /* Style the submit button with gold gradient */
  .form-container button[type="submit"] {
    background: linear-gradient(
      135deg,
      #c9a961 0%,
      #f4e4a6 50%,
      #c9a961 100%
    ) !important;
    color: #000 !important;
    border: none !important;

    &:hover {
      background: linear-gradient(
        135deg,
        #b89551 0%,
        #e4d496 50%,
        #b89551 100%
      ) !important;
      color: #000 !important;
    }
  }
`;

const StyledButtonWrapper = styled.div`
  a {
    background: linear-gradient(
      135deg,
      #c9a961 0%,
      #f4e4a6 50%,
      #c9a961 100%
    ) !important;
    padding: 1rem 3.5rem !important;
    font-size: 1.15rem !important;
    border-radius: 10px !important;
    min-width: 250px !important;

    &:hover {
      transform: translateY(-2px) !important;
      box-shadow: 0 6px 16px rgba(201, 169, 97, 0.5) !important;
      background: linear-gradient(
        135deg,
        #c9a961 0%,
        #f4e4a6 50%,
        #c9a961 100%
      ) !important;
    }

    @media (max-width: 768px) {
      max-width: 350px !important;
      padding: 1rem 2rem !important;
      font-size: 1.05rem !important;
      min-width: unset !important;
    }
  }
`;

export default ScavengerHuntDynamic2025;
